"use client";

import { c as _c } from "react/compiler-runtime";
import { DotsVertical } from "@untitledui/icons";
import type { FC, RefAttributes } from "react";
import type { ButtonProps as AriaButtonProps, MenuItemProps as AriaMenuItemProps, MenuProps as AriaMenuProps, PopoverProps as AriaPopoverProps, SeparatorProps as AriaSeparatorProps } from "react-aria-components";
import { Button as AriaButton, Header as AriaHeader, Menu as AriaMenu, MenuItem as AriaMenuItem, MenuSection as AriaMenuSection, MenuTrigger as AriaMenuTrigger, Popover as AriaPopover, Separator as AriaSeparator, SubmenuTrigger as AriaSubmenuTrigger } from "react-aria-components";
import type { SubmenuTriggerProps } from "react-aria-components";
import { cx } from "@/utils/cx";
interface DropdownItemProps extends AriaMenuItemProps {
  /** The label of the item to be displayed. */
  label?: string;
  /** An addon to be displayed on the right side of the item. */
  addon?: string;
  /** If true, the item will not have any styles. */
  unstyled?: boolean;
  /** An icon to be displayed on the left side of the item. */
  icon?: FC<{
    className?: string;
  }>;
}
const DropdownItem = t0 => {
  const $ = _c(21);
  let Icon;
  let addon;
  let children;
  let label;
  let props;
  let unstyled;
  if ($[0] !== t0) {
    ({
      label,
      children,
      addon,
      icon: Icon,
      unstyled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = addon;
    $[3] = children;
    $[4] = label;
    $[5] = props;
    $[6] = unstyled;
  } else {
    Icon = $[1];
    addon = $[2];
    children = $[3];
    label = $[4];
    props = $[5];
    unstyled = $[6];
  }
  if (unstyled) {
    let t1;
    if ($[7] !== label || $[8] !== props) {
      t1 = <AriaMenuItem id={label} textValue={label} {...props} />;
      $[7] = label;
      $[8] = props;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  let t1;
  if ($[10] !== props) {
    t1 = state => cx("group block cursor-pointer px-1.5 py-px outline-hidden", state.isDisabled && "cursor-not-allowed", typeof props.className === "function" ? props.className(state) : props.className);
    $[10] = props;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== Icon || $[13] !== addon || $[14] !== children || $[15] !== label) {
    t2 = state_0 => <div className={cx("relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear", !state_0.isDisabled && "group-hover:bg-primary_hover", state_0.isFocused && "bg-primary_hover", state_0.isFocusVisible && "outline-2 -outline-offset-2")}>{Icon && <Icon aria-hidden="true" className={cx("mr-2 size-4 shrink-0 stroke-[2.25px]", state_0.isDisabled ? "text-fg-disabled" : "text-fg-quaternary")} />}<span className={cx("grow truncate text-sm font-semibold", state_0.isDisabled ? "text-disabled" : "text-secondary", state_0.isFocused && "text-secondary_hover")}>{label || (typeof children === "function" ? children(state_0) : children)}</span>{addon && <span className={cx("ml-3 shrink-0 rounded px-1 py-px text-xs font-medium ring-1 ring-secondary ring-inset", state_0.isDisabled ? "text-disabled" : "text-quaternary")}>{addon}</span>}</div>;
    $[12] = Icon;
    $[13] = addon;
    $[14] = children;
    $[15] = label;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== props || $[18] !== t1 || $[19] !== t2) {
    t3 = <AriaMenuItem {...props} className={t1}>{t2}</AriaMenuItem>;
    $[17] = props;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
};
interface DropdownMenuProps<T extends object> extends AriaMenuProps<T> {}
const DropdownMenu = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = state => cx("h-min overflow-y-auto py-1 outline-hidden select-none", typeof props.className === "function" ? props.className(state) : props.className);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== t0) {
    t1 = <AriaMenu disallowEmptySelection={true} selectionMode="single" {...props} className={t0} />;
    $[2] = props;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
interface DropdownPopoverProps extends AriaPopoverProps {}
const DropdownPopover = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = state => cx("w-62 origin-(--trigger-anchor-point) overflow-auto rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt will-change-transform", state.isEntering && "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5", state.isExiting && "duration-100 ease-out animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5", typeof props.className === "function" ? props.className(state) : props.className);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== t0) {
    t1 = <AriaPopover placement="bottom right" {...props} className={t0}>{props.children}</AriaPopover>;
    $[2] = props;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const DropdownSeparator = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.className) {
    t0 = cx("my-1 h-px w-full bg-border-secondary", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== t0) {
    t1 = <AriaSeparator {...props} className={t0} />;
    $[2] = props;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const DropdownSubmenuTrigger = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <AriaSubmenuTrigger {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const DropdownDotsButton = props => {
  const $ = _c(6);
  let t0;
  if ($[0] !== props) {
    t0 = state => cx("relative flex size-10 cursor-pointer items-center justify-center rounded-md text-fg-quaternary outline-focus-ring transition-colors duration-100 ease-linear", (state.isPressed || state.isHovered) && "text-fg-quaternary_hover", (state.isPressed || state.isFocusVisible) && "outline-2 outline-offset-2", typeof props.className === "function" ? props.className(state) : props.className);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DotsVertical className="size-5 transition-inherit-all" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props || $[4] !== t0) {
    t2 = <AriaButton {...props} aria-label="Open menu" className={t0}>{t1}</AriaButton>;
    $[3] = props;
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const Dropdown = {
  Root: AriaMenuTrigger,
  Popover: DropdownPopover,
  Menu: DropdownMenu,
  Section: AriaMenuSection,
  SectionHeader: AriaHeader,
  Item: DropdownItem,
  Separator: DropdownSeparator,
  SubmenuTrigger: DropdownSubmenuTrigger,
  DotsButton: DropdownDotsButton
};